/**
 * ProspectFilterHeader Component - MA-ARCH-162
 * 
 * Extracted from ScriptPlay.jsx - Header for the Prospect Info panel.
 * Contains the prospect filter dropdown and edit button.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pencil } from 'lucide-react';

/**
 * @param {Object} props
 * @param {string} props.filter - Current filter value ('all', 'household', or prospect id)
 * @param {Function} props.onFilterChange - Callback when filter changes
 * @param {Array} props.prospects - Run prospects [{id, name, sort_order}]
 * @param {string} props.runId - Current run ID
 * @param {Function} props.onEditProspects - Callback to open prospect edit dialog
 */
export function ProspectFilterHeader({
  filter,
  onFilterChange,
  prospects = [],
  runId,
  onEditProspects,
}) {
  return (
    <div className="space-y-5" data-testid="prospect-filter-block">
      <div
        className="flex items-center justify-between gap-2"
        data-testid="prospect-filter-row"
      >
        <div
          className="flex w-full items-center gap-2"
          data-testid="prospect-filter-control"
        >
          <span
            className="text-sm font-medium text-slate-200/80"
            data-testid="prospect-filter-label"
          >
            Show:
          </span>

          <Select value={filter} onValueChange={onFilterChange}>
            <SelectTrigger
              className="h-9 w-full bg-white/5 text-slate-100 ring-1 ring-white/10"
              data-testid="prospect-filter-select"
            >
              <SelectValue placeholder="View fields for..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="prospect-filter-all">
                All
              </SelectItem>
              <SelectItem value="household" data-testid="prospect-filter-household">
                Household
              </SelectItem>
              {(prospects || []).map((p, idx) => (
                <SelectItem
                  key={p.id}
                  value={p.id}
                  data-testid={`prospect-filter-prospect-${p.sort_order ?? idx}`}
                  data-prospect-id={p.id}
                >
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {runId && prospects.length > 0 && (
          <Button
            variant="secondary"
            size="icon"
            className="h-9 w-9 rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
            onClick={onEditProspects}
            data-testid="edit-prospects-button"
            title="Edit prospect names"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="h-px bg-white/10" aria-hidden />
    </div>
  );
}

export default ProspectFilterHeader;
